import {Dictionary} from '@malt/types-utils';
import uniqBy from 'lodash/uniqBy';
import {defineStore, Pinia, StateTree, StoreDefinition, StoreGeneric} from 'pinia';
import {ApiRequestHandlerOptions, AppContextOutput, HttpMethod, RequestHandler, Resource, ResourceLink} from './types';
import {$fetch, cleanResourceLinks, findResourceLink} from './utils';

export {$fetch} from './utils';

let pinia: Pinia;

interface DefineAppContextOptions<TContext extends Resource, TEntries extends string[]> {
    useAppContext?: StoreDefinition<'appContext', StateTree>;
    baseUrl: string;
    defaultState?: TContext;
    entries: [...TEntries];
    onResolvedContext?: (ctx: {
        createEndpoint: AppContextOutput<TContext, TEntries>['createEndpoint'];
        addLinks(links: ResourceLink[]): void;
        pinia: Pinia;
    }) => Promise<void>;
}

/**
 *
    This tool will make it easier to bootstrap new Vue applications
    without needing `vuejs-starter`

    All setup of context registry is now under just one function `defineAppContext`

    What will this function do:

    Exemple:

    ```ts
    // src/config/index.ts
    import {defineAppContext} from '@malt/vue-context';

    export const {loadApiContext, createEndpoint, useAppContext, endpointExists, getEndpointUrl} = defineAppContext(
        {
            baseUrl: '/api/my-app',
            // Default state of useAppContext Pinia store
            defaultState: {
                _links: [],
            } as MyAppContextResource,
            // Called when context is fetched and stored
            onResolvedContext: async ({createEndpoint, addLinks, pinia}) => {
                // Exemple with more links to fetch from another endpoint
                const paymentContextStore = usePaymentContext(pinia);
                const paymentContextRequest = createEndpoint<PaymentContextResource>('paymentContext');
                const {_links, ...paymentContext} = await paymentContextRequest();
                paymentContextStore.$patch(paymentContext);
                addLinks(_links);
            },
            // Declare all possible entries to have autocomplete
            entries: [
                'paymentContext',
                'confirmationPage',
                'cards',
            ],
        },
    );
```

 */
export function defineAppContext<TContext extends Resource, TEntries extends string[]>({
    defaultState,
    baseUrl,
    onResolvedContext,
    useAppContext: userProvidedStore,
}: DefineAppContextOptions<TContext, TEntries>): AppContextOutput<TContext, TEntries> {
    const _useAppContext = userProvidedStore ?? createAppContext();
    let testing = false;
    /** Fetch the `self` entry to get the base app context, and store it in the Pinia store */
    async function loadApiContext(_pinia: Pinia, _testing = false) {
        pinia = _pinia;
        testing = _testing;
        if (_testing) {
            return;
        }
        const appContext = useAppContext(_pinia);
        appContext.loading = true;

        try {
            await fetchContext();
        } catch (e) {
            console.error(e);
            return Promise.reject(e);
        } finally {
            appContext.loading = false;
        }
    }

    async function fetchContext() {
        const store = useAppContext(pinia);
        const apiContext = await getApiContext();

        if (!apiContext) {
            return console.error(`No API context found for ${baseUrl}`);
        }

        if (apiContext?._links) {
            const {_links, ...rest} = apiContext;
            store.$patch({_links: _links ? cleanResourceLinks(apiContext._links) : [], ...rest});
        }

        if (onResolvedContext) {
            await onResolvedContext({createEndpoint, addLinks, pinia});
        }
    }

    function createEndpoint<TResult = void, TParams extends Dictionary = never>(
        endpoint: TEntries[number],
        options?: ApiRequestHandlerOptions,
    ): RequestHandler<TResult, TParams> {
        async function returnedRequestHandler(params?: TParams): Promise<TResult> {
            const appContext = useAppContext(pinia);
            const providedLinks = options?.overrideLinks ?? appContext._links;
            const logger = options?.logger ?? true;
            const resourceLink = findResourceLink(endpoint, providedLinks);

            if ((!options?.overrideLinks && !appContext._links) || !resourceLink) {
                console.warn(`Cannot find endpoint ${endpoint} in registry. Try checking if it exists at runtime with the "endpointExists" export`);
                return null as any;
            } else {
                if (options?.method) {
                    resourceLink.type = options.method;
                }

                let url = resourceLink.href;
                let paramsWithoutPathVariable: Record<string, any> | undefined;

                const ARG_REGEXP = /{.*?}/g;
                if (url.match(ARG_REGEXP) && params) {
                    const paramsKeys = Object.keys(params);
                    if (paramsKeys.length > 0) {
                        paramsWithoutPathVariable = {};

                        paramsKeys.forEach((key) => {
                            const routeParam = `{${key}}`;
                            if (url.includes(routeParam)) {
                                url = url.replace(routeParam, params[key]);
                            } else {
                                paramsWithoutPathVariable![key] = params[key];
                            }
                        });
                    }
                } else {
                    paramsWithoutPathVariable = params;
                }

                return (await $fetch({method: resourceLink.type!, url}, paramsWithoutPathVariable, logger)) as any;
            }
        }
        return returnedRequestHandler as any;
    }
    // Pinia store with app context
    function createAppContext() {
        return defineStore('appContext', {
            state: () => (({...defaultState, loading: false} as unknown) as TContext & {loading: boolean}),
        });
    }

    function endpointExists(key: TEntries[number], overrideLinks?: ResourceLink[]): boolean {
        if (testing) {
            return true;
        }
        const appContext = useAppContext(pinia);
        return !!findResourceLink(key, overrideLinks ?? appContext._links);
    }

    function getEndpointUrl(key: TEntries[number], overrideLinks?: ResourceLink[]): string | undefined {
        if (testing) {
            return '';
        }
        const appContext = useAppContext(pinia);

        return findResourceLink(key, overrideLinks ?? appContext._links)?.href;
    }

    /** Fetch API context registry */
    async function getApiContext(): Promise<TContext | null> {
        return await $fetch({url: baseUrl, method: HttpMethod.GET});
    }

    function useAppContext(_pinia?: Pinia | null, hot?: StoreGeneric) {
        return _useAppContext(_pinia ?? pinia, hot);
    }

    function addLinks(links: ResourceLink[]) {
        const newLinks = useAppContext()._links.concat(links);
        useAppContext()._links = uniqBy(newLinks, 'rel');
    }

    return {
        useAppContext: useAppContext as any,
        loadApiContext,
        reloadContext: fetchContext,
        addLinks,
        createEndpoint,
        endpointExists,
        getEndpointUrl,
    };
}
